import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import history from '../../helpers/history';
import { submitLoanApplication } from '../../apis/loan';
import { getLoanTypeIdFromLoanTypeName, handleApiError, setAuthHeader } from '../../helpers';
import { LoanDto, LoanFormValues, LoanType, LoanTypeId } from '../../interfaces';

const successHandler = async () => {
  const value = await Swal.fire({
    title: 'Success!',
    text: 'Your loan application was submitted',
    icon: 'success',
  });

  if (value.isConfirmed || value.isDismissed) {
    history.push('/loans');
  }
};

export const useSubmitLoan = () => {
  const [submitting, setSubmitting] = useState(false);

  const submitLoanHandler = useCallback(async (payload: LoanFormValues) => {
    try {
      const loan_type_id = getLoanTypeIdFromLoanTypeName(payload.loan_type);
      const loanDto: LoanDto = {
        loan_type_id,
        loan_detail: {
          name: payload.project_name,
          amount: +payload.loan_amount,
          tenure: +payload.loan_tenure,
        },
      };

      if (payload.loan_type === LoanType.MORTGAGE) {
        const is_new_construction = payload.is_new_construction ?? false;
        loanDto.mortgage = {
          down_payment: payload.down_payment ? +payload.down_payment : null,
          property: {
            type: payload.property_type!,
            title: payload.property_title!,
            location: {
              city: payload.property_city,
              state_id: +payload.property_state,
              address: payload.property_address,
              country_id: +payload.property_country,
            },
          },
          purchase_price: +payload.purchase_price!,
          is_new_construction,
          contact_person: {
            name: payload.contact_name,
            email: payload.contact_email,
            phone: payload.contact_phone,
          },
        };

        if (is_new_construction) {
          loanDto.mortgage.developer = {
            name: payload.developer_name!,
            location: {
              address: payload.developer_address!,
              city: payload.developer_city!,
              state_id: +payload.developer_state!,
              country_id: +payload.developer_country!,
            },
          };
        }
      }

      if (payload.loan_type === LoanType.EQUITY_FINANCE) {
        loanDto.equity = {
          estimated_property_value: +payload.property_value!,
          valuation_method: payload.valued_method!,
          property_address: {
            address: payload.property_address,
            city: payload.property_city,
            state_id: +payload.property_state,
            country_id: +payload.property_country,
          },
        };
      }

      if (payload.loan_type === LoanType.RENT) {
        loanDto.rent = {
          other_cost: payload?.other_cost ? +payload.other_cost : null,
          include_agent_cost: payload.include_agent_cost ?? false,
          home_owner: {
            name: payload.home_owners_name!,
            email: payload.home_owners_email,
            phone: payload.home_owners_phone!,
          },
          property_address: {
            address: payload.property_address,
            city: payload.property_city,
            state_id: +payload.property_state,
            country_id: +payload.property_country,
          },
        };
      }

      setSubmitting(true);
      await submitLoanApplication(loanDto);

      setSubmitting(false);
      successHandler();
    } catch (error) {
      console.log(error);
      setSubmitting(false);
      toast.error(handleApiError(error));
    }
  }, []);

  return { submitLoanHandler, submitting };
};
